<script setup>
import { computed, ref, watch } from 'vue'
import CustomerServiceInputBox from './CustomerServiceInputBox.vue'
import CustomerServiceMessageList from './CustomerServiceMessageList.vue'
import { useCustomerService } from '@/composables/useCustomerService'
import { useCustomerServiceHub } from '@/composables/useCustomerServiceHub'

const props = defineProps({
  context: {
    type: Object,
    default: () => ({}),
  },
  visible: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])
const clearSignal = ref(0)

const {
  activeConversationId,
  conversations,
  loadingConversations,
  loadingMessages,
  loadingStarters,
  messages,
  starterPayload,
  sending,
  transferingToHuman,
  initialize,
  loadConversations,
  loadConversationStarters,
  loadMessages,
  requestHumanService,
  selectConversation,
  sendMessage,
  startNewConversation,
  submitFeedback,
} = useCustomerService()

const customerServiceHub = useCustomerServiceHub({
  async onConversationChanged(payload) {
    const conversationRecordId = Number(payload?.ConversationRecordId || payload?.conversationRecordId || 0)
    if (!props.visible || !conversationRecordId || conversationRecordId !== activeConversationId.value) {
      return
    }

    await loadMessages(conversationRecordId)
    await loadConversations(false)
  },
})

const currentModuleLabel = computed(() => props.context?.currentModule || '当前页面')
const contextSignature = computed(() => JSON.stringify({
  currentModule: props.context?.currentModule || '',
  currentPath: props.context?.currentPath || '',
  businessCode: props.context?.businessCode || '',
  recordId: props.context?.recordId || '',
  recordTitle: props.context?.recordTitle || '',
}))
const activeConversation = computed(() => conversations.value.find(
  item => item.conversationRecordId === activeConversationId.value
) || null)
const isPendingHuman = computed(() => activeConversation.value?.status === 10)
const isManualService = computed(() => activeConversation.value?.status === 20)
const canTransferToHuman = computed(() => Boolean(
  activeConversation.value?.conversationRecordId && !isPendingHuman.value && !isManualService.value
))
const transferButtonText = computed(() => {
  if (isPendingHuman.value) {
    return '已转人工'
  }

  if (isManualService.value) {
    return '人工处理中'
  }

  return '转人工'
})
const inputPlaceholder = computed(() => {
  if (isPendingHuman.value) {
    return '你可以继续补充问题，人工客服接入后会优先查看'
  }

  if (isManualService.value) {
    return '继续输入问题，人工客服会直接回复你'
  }

  return '请输入问题，或直接输入采购单号、入库单号、出库单号、检验单号、供应商编码、物料编码进行查询'
})

watch(
  () => props.visible,
  async visible => {
    if (!visible) {
      await customerServiceHub.disconnect()
      return
    }

    await initialize()
    await loadConversationStarters(props.context)
    await customerServiceHub.connect()
    if (activeConversationId.value) {
      await customerServiceHub.watchConversation(activeConversationId.value)
    }
  },
  { immediate: true }
)

watch(
  contextSignature,
  async () => {
    if (!props.visible) {
      return
    }

    await loadConversationStarters(props.context)
  }
)

function handleClose() {
  emit('close')
}

function handleSelectConversation(conversation) {
  selectConversation(conversation.conversationRecordId)
  customerServiceHub.watchConversation(conversation.conversationRecordId)
}

async function handleStartNewConversation() {
  startNewConversation()
  await loadConversationStarters(props.context)
}

function handleFeedback({ messageId, rating }) {
  submitFeedback(messageId, rating)
}

function handleQuickQuestion(question) {
  handleSendMessage(question)
}

async function handleTransferToHuman() {
  if (!canTransferToHuman.value) {
    return
  }

  await requestHumanService({
    conversationRecordId: activeConversation.value?.conversationRecordId,
    transferReason: '用户在客服面板主动转人工',
  })
}

async function handleSendMessage(message) {
  clearSignal.value += 1
  const success = await sendMessage({
    ...props.context,
    message,
  })

  return success
}

watch(
  () => activeConversationId.value,
  async conversationRecordId => {
    if (!props.visible || !conversationRecordId) {
      return
    }

    await customerServiceHub.watchConversation(conversationRecordId)
  }
)
</script>

<template>
  <div class="customer-service-panel">
    <div class="customer-service-panel__header">
      <div>
        <div class="customer-service-panel__eyebrow">智能客服</div>
        <div class="customer-service-panel__title">{{ currentModuleLabel }}</div>
      </div>
      <div class="customer-service-panel__actions">
        <el-button
          text
          :disabled="!canTransferToHuman"
          :loading="transferingToHuman"
          @click="handleTransferToHuman"
        >
          {{ transferButtonText }}
        </el-button>
        <el-button text @click="handleStartNewConversation">新建会话</el-button>
        <el-button text @click="handleClose">关闭</el-button>
      </div>
    </div>

    <div class="customer-service-panel__body">
      <aside class="customer-service-panel__sidebar">
        <div class="customer-service-panel__sidebar-title">历史会话</div>
        <div v-if="loadingConversations" class="customer-service-panel__empty">
          正在加载...
        </div>
        <div v-else-if="!conversations.length" class="customer-service-panel__empty">
          暂无会话记录
        </div>
        <template v-else>
          <button
            v-for="conversationItem in conversations"
            :key="conversationItem.conversationRecordId"
            type="button"
            class="customer-service-panel__conversation"
            :class="{ 'is-active': activeConversationId === conversationItem.conversationRecordId }"
            @click="handleSelectConversation(conversationItem)"
          >
            <span class="conversation-title">{{ conversationItem.title || '未命名会话' }}</span>
            <span class="conversation-preview">
              {{ conversationItem.lastAnswerPreview || conversationItem.lastQuestion || '暂无摘要' }}
            </span>
            <span
              v-if="conversationItem.statusText"
              class="conversation-status"
              :class="{
                'is-pending': conversationItem.status === 10,
                'is-manual': conversationItem.status === 20,
              }"
            >
              {{ conversationItem.statusText }}
            </span>
          </button>
        </template>
      </aside>

      <section class="customer-service-panel__content">
        <CustomerServiceMessageList
          :loading="loadingMessages"
          :loading-starters="loadingStarters"
          :sending="sending"
          :messages="messages"
          :welcome-message="starterPayload.welcomeMessage"
          :capability-labels="starterPayload.capabilityLabels"
          :starter-questions="starterPayload.questions"
          :show-starter-state="true"
          :conversation-status="activeConversation?.status || 0"
          :conversation-status-text="activeConversation?.statusText || ''"
          :human-agent-name="activeConversation?.humanAgentName || ''"
          :transfer-time="activeConversation?.transferToHumanAt || ''"
          @feedback="handleFeedback"
          @quick-question="handleQuickQuestion"
        />
        <CustomerServiceInputBox
          :sending="sending"
          :clear-signal="clearSignal"
          :placeholder="inputPlaceholder"
          :manual-mode="isPendingHuman || isManualService"
          @submit="handleSendMessage"
        />
      </section>
    </div>
  </div>
</template>

<style scoped lang="scss">
.customer-service-panel {
  position: fixed;
  right: 24px;
  bottom: 88px;
  width: min(940px, calc(100vw - 32px));
  height: min(680px, calc(100vh - 120px));
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 26px 80px rgba(17, 37, 87, 0.22);
  border: 1px solid rgba(104, 134, 219, 0.18);
  z-index: 2200;
}

.customer-service-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  color: #ffffff;
  background: linear-gradient(135deg, #244dcb 0%, #4d7dff 55%, #79b4ff 100%);
}

.customer-service-panel__eyebrow {
  font-size: 12px;
  opacity: 0.82;
  letter-spacing: 0.12em;
}

.customer-service-panel__title {
  margin-top: 6px;
  font-size: 22px;
  font-weight: 700;
}

.customer-service-panel__actions {
  display: flex;
  gap: 8px;

  :deep(.el-button) {
    color: #ffffff;
  }

  :deep(.el-button.is-disabled) {
    color: rgba(255, 255, 255, 0.66);
  }
}

.customer-service-panel__body {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
}

.customer-service-panel__sidebar {
  padding: 18px 14px;
  border-right: 1px solid #e4ebfb;
  background: linear-gradient(180deg, #eff4ff 0%, #f8fbff 100%);
  overflow-y: auto;
}

.customer-service-panel__sidebar-title {
  padding: 0 8px 12px;
  color: #1f3156;
  font-size: 14px;
  font-weight: 700;
}

.customer-service-panel__conversation {
  width: 100%;
  padding: 12px 12px 14px;
  margin-bottom: 10px;
  border: 1px solid transparent;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.74);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.customer-service-panel__conversation:hover,
.customer-service-panel__conversation.is-active {
  border-color: rgba(71, 114, 255, 0.28);
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(56, 92, 186, 0.12);
}

.conversation-title,
.conversation-preview,
.conversation-status {
  display: block;
}

.conversation-title {
  color: #203253;
  font-size: 14px;
  font-weight: 700;
}

.conversation-preview {
  margin-top: 8px;
  color: #6d7c99;
  font-size: 12px;
  line-height: 1.6;
}

.conversation-status {
  margin-top: 10px;
  font-size: 11px;
  font-weight: 700;
  color: #4a66ba;
}

.conversation-status.is-pending {
  color: #bf6b12;
}

.conversation-status.is-manual {
  color: #13886c;
}

.customer-service-panel__content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background:
    radial-gradient(circle at top right, rgba(77, 115, 255, 0.08), transparent 24%),
    linear-gradient(180deg, #f8fbff 0%, #f2f6fd 100%);
}

.customer-service-panel__empty {
  padding: 36px 16px;
  color: #7e8ba7;
  text-align: center;
  font-size: 13px;
}

@media (max-width: 960px) {
  .customer-service-panel {
    right: 16px;
    bottom: 78px;
    width: calc(100vw - 32px);
    height: calc(100vh - 110px);
  }

  .customer-service-panel__body {
    grid-template-columns: 1fr;
  }

  .customer-service-panel__sidebar {
    max-height: 180px;
    border-right: 0;
    border-bottom: 1px solid #e4ebfb;
  }
}
</style>
